import { useSendMonthlyExpenseMutation } from '@/app/services/monthlyExpenseApi';
import { usePeriodsQuery } from '@/app/services/periodApi';
import { useGenerateSignedLinkQuery } from '@/app/services/processApi';
import { Period } from '@app/models/period';
import formatDateMonthYear, { formatDate } from '@helpers/date';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import { IconButton, styled, Tooltip, tooltipClasses, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type CallbackUpdateFunction = (a: Period) => void;
type TranslateFunction = (a: string) => string;
type CallbackUpdateStateFunction = (a: SetStateAction<string | null>) => void;

type ReprocessTooltipProps = {
  children: React.ReactElement<any, any>;
  open: boolean;
  reprocess: CallbackUpdateFunction;
  period: Period;
  t: TranslateFunction;
  setOpen: () => void;
};

function FilePathLink(params: { period: string }) {
  const { period } = params;
  const { data: file } = useGenerateSignedLinkQuery(period);
  return (
    <div>
      {file?.filePath && (
        <div
          style={{
            width: '135px',
            textAlign: 'start',
            marginTop: '10px',
          }}
        >
          <a
            href={file?.filePath}
            style={{
              textTransform: 'none',
              textDecoration: 'none',
              display: 'flex',
            }}
          >
            <DownloadIcon
              sx={{
                width: '16px',
                height: '16px',
                color: 'black',
              }}
              fontSize="small"
            />
            <Typography sx={{ fontSize: '13px', color: '#666666', marginLeft: '8px' }}>Download</Typography>
          </a>
        </div>
      )}
    </div>
  );
}
function SendOverhead(params: { period: string; onClose: () => void }) {
  const { t } = useTranslation(['processing', 'common']);
  const { period, onClose } = params;
  const { data: file } = useGenerateSignedLinkQuery(period);

  const [sendMonthlyExpense] = useSendMonthlyExpenseMutation();

  const handleSend = async () => {
    onClose();
    if (!period) return;
    await sendMonthlyExpense(period);
    toast.success(t('monthly.expense.sent'));
  };
  return (
    <div>
      {file?.filePath && (
        <div
          role="presentation"
          style={{
            width: '135px',
            display: 'flex',
            textAlign: 'start',
            marginTop: '10px',
            cursor: 'pointer',
          }}
          onClick={() => handleSend()}
          onKeyDown={() => handleSend()}
        >
          <ArrowOutwardIcon
            sx={{
              width: '16px',
              height: '16px',
              color: 'black',
            }}
            fontSize="small"
          />
          <Typography sx={{ fontSize: '13px', color: '#666666', marginLeft: '8px' }}>Enviar Folha</Typography>
        </div>
      )}
    </div>
  );
}

const ReprocessTooltip = styled(({ children, open, reprocess, period, t, setOpen }: ReprocessTooltipProps) => (
  <Tooltip
    arrow
    PopperProps={{
      disablePortal: true,
    }}
    disableFocusListener
    disableHoverListener
    disableTouchListener
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: '#FFFFFF',

          border: '1px solid #D9D9D9',
          '& .MuiTooltip-arrow': {
            color: '#FFFFFF',
            '&:before': {
              border: '1px solid #D9D9D9',
            },
          },
        },
      },
    }}
    open={open}
    placement="left"
    title={
      <div
        style={{
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          role="presentation"
          style={{
            width: '125px',
            textAlign: 'start',
            cursor: 'pointer',
            display: 'flex',
            marginTop: '10px',
          }}
          onClick={() => reprocess(period)}
          onKeyDown={() => reprocess(period)}
        >
          <SyncIcon
            sx={{
              width: '16px',
              height: '16px',
              color: 'black',
            }}
            fontSize="small"
          />
          <Typography
            sx={{
              fontSize: '13px',
              color: '#666666',
              marginLeft: '8px',
            }}
          >
            {t('reprocess')}
          </Typography>
        </div>
        {open && <FilePathLink period={period.date} />}
        {open && <SendOverhead period={period.date} onClose={setOpen} />}
      </div>
    }
  >
    {children}
  </Tooltip>
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    padding: '1px',
  },
}));

const columns = (
  t: TranslateFunction,
  language: string,
  openTooltip: string | null,
  setOpenTooltip: CallbackUpdateStateFunction,
  reprocess: CallbackUpdateFunction,
) =>
  [
    {
      field: 'date',
      headerName: t('period'),
      width: 150,
      valueFormatter: ({ value }) => formatDateMonthYear(value, language),
      sortable: false,
    },
    {
      field: 'processingDate',
      headerName: t('processing.date'),
      width: 180,
      sortable: false,
      valueFormatter: ({ value }) => formatDate(value, language),
    },
    {
      field: 'processedBy',
      headerName: t('processed.by'),
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'directSharingProcessed',
      headerName: t('direct'),
      width: 90,
      sortable: false,
      valueFormatter: ({ value }) => (value ? t('common:yes') : t('common:no')),
    },
    {
      field: 'indirectSharingProcessed',
      headerName: t('indirect'),
      width: 100,
      sortable: false,
      valueFormatter: ({ value }) => (value ? t('common:yes') : t('common:no')),
    },
    {
      field: 'directSharingSynchronized',
      headerName: t('synchronized'),
      width: 120,
      sortable: false,
      valueFormatter: ({ value }) => (value ? t('common:yes') : t('common:no')),
    },
    {
      field: 'id',
      headerName: t('common:actions'),
      sortable: false,
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <ReprocessTooltip
          t={t}
          open={openTooltip === params.id}
          setOpen={() => setOpenTooltip(null)}
          reprocess={reprocess}
          period={params.row as Period}
        >
          <IconButton
            onClick={() => {
              if (openTooltip) {
                setOpenTooltip(null);
              } else {
                setOpenTooltip(params.id as string);
              }
            }}
          >
            <MoreVertIcon className="action" />
          </IconButton>
        </ReprocessTooltip>
      ),
    },
  ] as GridColDef[];

type RequestGridParams = {
  pageOptions: number[];
  setSelectedPeriod: Dispatch<SetStateAction<Period | undefined>>;
  setOpenNewProcessing: Dispatch<SetStateAction<boolean>>;
};

function PeriodHistory(params: RequestGridParams) {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const { i18n, t } = useTranslation(['processing', 'common']);
  const { pageOptions, setSelectedPeriod, setOpenNewProcessing } = params;

  const localizedTextsMap = {
    footerRowSelected: (count: number) =>
      count !== 1
        ? `${count.toLocaleString()} ${t('common:lines.selected')}`
        : `${count.toLocaleString()} ${t('common:line.selected')}`,
    MuiTablePagination: {
      labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
        `${from} - ${to} ${t('common:of')} ${count}`,
    },
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const { data, isSuccess, isLoading } = usePeriodsQuery({
    page: paginationModel.page,
    size: paginationModel.pageSize,
    timestamp: 0,
  });

  const reprocess = async (period: Period) => {
    setSelectedPeriod(period);
    setOpenNewProcessing(true);
  };

  const myCustomNoRows = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        height: '360px',
        alignItems: 'center',
      }}
    >
      <Typography>{t('common:no.results.found')}</Typography>
    </div>
  );

  return (
    <div
      style={{
        height: 'calc(100vh - 360px)',
        marginTop: '16px',
      }}
    >
      <div style={{ margin: '30px 0px 10px 0px', color: '#AAAAAA' }}>{t('history')}</div>
      {isSuccess && (
        <DataGrid
          disableRowSelectionOnClick
          slots={{ noRowsOverlay: myCustomNoRows }}
          disableColumnMenu
          disableColumnFilter
          sx={{
            border: 'none',
            '& .MuiCheckbox-root.Mui-checked': {
              color: '#ff7b00',
            },
            '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'none',
            },
          }}
          rowHeight={40}
          columnHeaderHeight={40}
          localeText={localizedTextsMap}
          rows={data.content.map((c) => ({ ...c, id: c.date }))}
          columns={columns(t, i18n.language, openTooltip, setOpenTooltip, reprocess)}
          pageSizeOptions={pageOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          loading={isLoading}
        />
      )}
    </div>
  );
}

export default PeriodHistory;
